// Offline mirror for Drive-mode users (iter 94, option A: browse only).
//
// Every successful `/api/plants` fetch writes the raw array into
// IndexedDB under a single object store keyed by user email so multiple
// signed-in accounts on the same device don't clobber each other.
//
// When the network is down (or the request throws), `loadPlants` reads
// from this mirror so users can at least browse their library and open
// plant details. Writes are blocked by the UI when `navigator.onLine`
// is false — no queue in this MVP.
//
// Kept intentionally tiny: single store, no versioning, no indexes.
// If the schema ever changes we can just bump `MIRROR_DB_VERSION` and
// wipe the store — an offline fallback is by definition a snapshot, not
// a source of truth.

const MIRROR_DB_NAME = 'PlantInventoryMirror';
const MIRROR_DB_VERSION = 1;
const MIRROR_STORE = 'library_snapshots';

function openMirror() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(MIRROR_DB_NAME, MIRROR_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(MIRROR_STORE)) {
        // Key path is the user's email (or 'anon' when we don't know
        // who's signed in). Storing the whole plants array under a
        // single record keeps writes atomic — no partial updates.
        db.createObjectStore(MIRROR_STORE, { keyPath: 'user_key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('mirror-open-failed'));
  });
}

/**
 * Persist the latest plants array so a subsequent offline reload can
 * fall back to it. Non-fatal — if IndexedDB is unavailable (Safari
 * private mode, quota) we swallow the error and keep the app running.
 *
 * @param {string} userKey — email or another stable identifier for the
 *   currently signed-in account. Snapshots for different accounts do
 *   not overwrite one another.
 * @param {Array<object>} plants — the raw response from `/api/plants`.
 */
export async function saveOfflineSnapshot(userKey, plants) {
  if (!Array.isArray(plants)) return;
  try {
    const db = await openMirror();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(MIRROR_STORE, 'readwrite');
      const store = tx.objectStore(MIRROR_STORE);
      const req = store.put({
        user_key: userKey || 'anon',
        plants,
        saved_at: Date.now(),
      });
      req.onsuccess = resolve;
      req.onerror = () => reject(req.error);
    });
    db.close();
  } catch (err) {
    // Non-fatal — the online app still works, we just won't have an
    // offline snapshot next time.
    if (typeof console !== 'undefined') {
      console.debug('offline snapshot save failed (non-fatal):', err?.message || err);
    }
  }
}

/**
 * Read the last saved snapshot for the given user. Returns null when
 * no snapshot exists or IndexedDB is unavailable — callers should
 * surface a friendlier error message in that case.
 *
 * @returns {Promise<{plants: Array<object>, saved_at: number} | null>}
 */
export async function loadOfflineSnapshot(userKey) {
  try {
    const db = await openMirror();
    const record = await new Promise((resolve, reject) => {
      const tx = db.transaction(MIRROR_STORE, 'readonly');
      const store = tx.objectStore(MIRROR_STORE);
      const req = store.get(userKey || 'anon');
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!record || !Array.isArray(record.plants)) return null;
    return { plants: record.plants, saved_at: record.saved_at };
  } catch {
    return null;
  }
}

/**
 * Return lightweight metadata about the stored snapshot for the given
 * user without loading the whole array into React state: plant count,
 * an approximate byte size (JSON length of the record), and when it
 * was saved. Returns null when no snapshot exists.
 *
 * @returns {Promise<{count: number, bytes: number, saved_at: number} | null>}
 */
export async function getOfflineSnapshotInfo(userKey) {
  try {
    const db = await openMirror();
    const record = await new Promise((resolve, reject) => {
      const tx = db.transaction(MIRROR_STORE, 'readonly');
      const store = tx.objectStore(MIRROR_STORE);
      const req = store.get(userKey || 'anon');
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!record || !Array.isArray(record.plants)) return null;
    let bytes = 0;
    try {
      bytes = new Blob([JSON.stringify(record.plants)]).size;
    } catch {
      bytes = JSON.stringify(record.plants).length;
    }
    return { count: record.plants.length, bytes, saved_at: record.saved_at };
  } catch {
    return null;
  }
}

/**
 * Delete the stored snapshot for the given user. Non-fatal — resolves
 * to true on success, false if IndexedDB was unavailable.
 */
export async function clearOfflineSnapshot(userKey) {
  try {
    const db = await openMirror();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(MIRROR_STORE, 'readwrite');
      const store = tx.objectStore(MIRROR_STORE);
      const req = store.delete(userKey || 'anon');
      req.onsuccess = resolve;
      req.onerror = () => reject(req.error);
    });
    db.close();
    return true;
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.debug('offline snapshot clear failed (non-fatal):', err?.message || err);
    }
    return false;
  }
}

/**
 * Optimistically apply a queued mutation to the offline mirror so the
 * next offline read reflects the pending change immediately. Best-effort
 * — a missing snapshot or unknown op is a no-op. Never bumps `saved_at`
 * (that stays anchored to the last real server sync, so the "Last synced"
 * chip keeps telling the truth). Returns the display names of affected
 * plants so the pending-sync UI can label the entry.
 *
 * @param {string} userKey
 * @param {{op:string, plantId?:string, ids?:string[], payload?:object}} m
 * @returns {Promise<{affectedNames: string[]}>}
 */
export async function applyMutationToMirror(userKey, m) {
  const result = { affectedNames: [], restore: [] };
  try {
    const db = await openMirror();
    const key = userKey || 'anon';
    const record = await new Promise((resolve, reject) => {
      const tx = db.transaction(MIRROR_STORE, 'readonly');
      const req = tx.objectStore(MIRROR_STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    if (!record || !Array.isArray(record.plants)) {
      db.close();
      return result;
    }
    let plants = record.plants;

    if (m.op === 'delete') {
      const orig = plants.find((p) => p.plant_id === m.plantId);
      if (orig) { result.affectedNames.push(orig.common_name || orig.scientific_name || 'Unnamed plant'); result.restore.push(orig); }
      plants = plants.filter((p) => p.plant_id !== m.plantId);
    } else if (m.op === 'archive') {
      const orig = plants.find((p) => p.plant_id === m.plantId);
      if (orig) { result.affectedNames.push(orig.common_name || orig.scientific_name || 'Unnamed plant'); result.restore.push(orig); }
      // Archived plants drop out of the active library list.
      plants = plants.filter((p) => p.plant_id !== m.plantId);
    } else if (m.op === 'bulk-delete') {
      const ids = new Set(m.ids || []);
      plants.forEach((p) => { if (ids.has(p.plant_id)) { result.affectedNames.push(p.common_name || p.scientific_name || 'Unnamed plant'); result.restore.push(p); } });
      plants = plants.filter((p) => !ids.has(p.plant_id));
    } else if (m.op === 'edit') {
      const pl = m.payload || {};
      plants = plants.map((p) => {
        if (p.plant_id !== m.plantId) return p;
        result.affectedNames.push(pl.common_name || p.common_name || 'Unnamed plant');
        result.restore.push(p);
        return {
          ...p,
          common_name: pl.common_name != null ? pl.common_name : p.common_name,
          scientific_name: pl.scientific_name != null ? pl.scientific_name : p.scientific_name,
          care_details: pl.care_details != null ? pl.care_details : p.care_details,
          incomplete: pl.incomplete != null ? pl.incomplete : p.incomplete,
        };
      });
    } else if (m.op === 'bulk-update') {
      const pl = m.payload || {};
      const ids = new Set(pl.plant_ids || []);
      const field = pl.field;
      const value = (field === 'family' || field === 'plant_type') ? pl.value : String(pl.value || '').toLowerCase();
      plants = plants.map((p) => {
        if (!ids.has(p.plant_id)) return p;
        result.affectedNames.push(p.common_name || p.scientific_name || 'Unnamed plant');
        result.restore.push(p);
        return { ...p, care_details: { ...(p.care_details || {}), [field]: value } };
      });
    } else if (m.op === 'add-photo') {
      // Photos aren't stored in the browse-only mirror, so nothing to
      // patch — just capture the plant name so the pending-sync UI can
      // label the queued photo upload.
      const orig = plants.find((p) => p.plant_id === m.plantId);
      if (orig) result.affectedNames.push(orig.common_name || orig.scientific_name || 'Unnamed plant');
      db.close();
      return result;
    } else {
      db.close();
      return result;
    }

    await new Promise((resolve, reject) => {
      const tx = db.transaction(MIRROR_STORE, 'readwrite');
      const req = tx.objectStore(MIRROR_STORE).put({ ...record, plants });
      req.onsuccess = resolve;
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result;
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.debug('mirror mutation failed (non-fatal):', err?.message || err);
    }
    return result;
  }
}

/**
 * Undo a queued mutation's optimistic mirror change by restoring the
 * original plant objects captured at enqueue time. Used when the user
 * discards a pending change before it syncs. Upserts each restore plant
 * back by plant_id (re-adds deletes/archives, reverts edits). Best-effort.
 */
export async function revertMutationInMirror(userKey, restorePlants) {
  if (!Array.isArray(restorePlants) || restorePlants.length === 0) return false;
  try {
    const db = await openMirror();
    const key = userKey || 'anon';
    const record = await new Promise((resolve, reject) => {
      const tx = db.transaction(MIRROR_STORE, 'readonly');
      const req = tx.objectStore(MIRROR_STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    if (!record || !Array.isArray(record.plants)) { db.close(); return false; }
    const byId = new Map(record.plants.map((p) => [p.plant_id, p]));
    restorePlants.forEach((p) => { if (p && p.plant_id) byId.set(p.plant_id, p); });
    const plants = Array.from(byId.values());
    await new Promise((resolve, reject) => {
      const tx = db.transaction(MIRROR_STORE, 'readwrite');
      const req = tx.objectStore(MIRROR_STORE).put({ ...record, plants });
      req.onsuccess = resolve;
      req.onerror = () => reject(req.error);
    });
    db.close();
    return true;
  } catch {
    return false;
  }
}
